import { RiTimeLine } from "@remixicon/react";
import prettyMilliseconds from "pretty-ms";
import React, { ReactElement } from "react";
import {
    parseDuration, Time,
    timeToMilisecondsWithValidation,
} from "../../../date";

import Select from "../../select/AsyncSelect";
import Styles from "./TimePicker.module.scss";

type DurationFormat = "colon" | "short";

export class Duration {
    private text: string;

    constructor(public readonly time: number, format: DurationFormat = "short") {
        this.text = !time ? "" : prettyMilliseconds(time, {
            colonNotation: format === "colon",
            verbose: true,
        });
    }

    public get short(): string {
        return prettyMilliseconds(this.time);
    }

    toString() {
        return this.text;
    }
}

const DEFAULT_TIMES = [
    new Duration(5 * Time.MINUTE),
    new Duration(10 * Time.MINUTE),
    new Duration(15 * Time.MINUTE),
    new Duration(30 * Time.MINUTE),
    new Duration(45 * Time.MINUTE),
    new Duration(Time.HOUR),
    new Duration(90 * Time.MINUTE),
    new Duration(2 * Time.HOUR),
    new Duration(4 * Time.HOUR),
    new Duration(8 * Time.HOUR),
    new Duration(24 * Time.HOUR),
    new Duration(72 * Time.HOUR),
];

interface Props<TRequired extends boolean> {
    onChange: (value: TRequired extends true ? Duration : (Duration | undefined)) => void;
    isRequired?: TRequired;
    value?: TRequired extends true ? (Duration | number) : (Duration | number | undefined);
    placeholder?: string;
    className?: string;
    format?: DurationFormat;
}

export default function DurationPicker<TRequired extends boolean>({
    onChange: onSelected, isRequired, value, placeholder, className, format,
}: Props<TRequired>): ReactElement {
    const time = (typeof value === "number" ? new Duration(value) : value as Duration);
    return (
        <Select
            className={className}
            minRequired={isRequired ? 1 : 0}
            isClearable={!isRequired}
            placeholder={placeholder}
            dropdownIcon={<RiTimeLine className={Styles["time-picker-dropdown"]} />}
            loadOptions={async (inputValue: string) => {
                const results = DEFAULT_TIMES.filter(current => current.toString().includes(inputValue));
                if (results.length === 0) {
                    const search = inputValue.toLowerCase();
                    let parsed;
                    if (search.includes(":")) {
                        parsed = timeToMilisecondsWithValidation(search);
                    } else {
                        parsed = parseDuration(search);
                    }
                    if (parsed) {
                        results.push(new Duration(parsed, format));
                    }
                }
                return results.map(item => ({
                    label: item.toString(),
                    value: item.time.toString(),
                    data: item,
                }));
            }}
            onChange={option => onSelected(option?.data as Duration)}
            value={time?.time ? {
                label: time.short,
                value: time.time.toString(),
            } : undefined}
        />
    );
}
DurationPicker.defaultProps = {
    isRequired: false,
    value: undefined,
    placeholder: undefined,
    className: "",
    format: "short",
};
